import { Injectable } from '@angular/core';
import { appendSegment, serializeTranscript, TranscriptSegment } from '@omni/services/voice-note/transcript-segments';
import { pcm16ToBytes, RECOGNISER_SAMPLE_RATE, toPcm16 } from './pcm-encoding';

/** How long any read may take, beyond the length of the audio it reads: connecting, decoding and settling. */
export const READ_LIMIT_BASE_MS = 15_000;

/** How long a read may take for each second of audio it reads. */
export const READ_TIME_PER_AUDIO_SECOND_MS = 1000;

/** How long a read of `seconds` of audio may take before it is given up. */
export function readLimitMs(seconds: number): number {
  return READ_LIMIT_BASE_MS + Math.max(0, seconds) * READ_TIME_PER_AUDIO_SECOND_MS;
}

/** The recogniser refused to start or cancelled the recognition with an error. */
export class RecognitionCancelledError extends Error {}

export interface RecordingTranscriberConfig {
  subscriptionKey: string;
  region: string;
  language?: string;
  customEndpointId?: string;
  phrases?: string[];
}

/**
 * Reads a recording made while recognition was out of reach.
 *
 * The same recogniser the live path uses, fed from a file instead of a microphone,
 * so a note recorded offline arrives in the same speaker turns as one dictated
 * online and reaches the same review.
 *
 * The recording is decoded here rather than handed over compressed: the SDK's
 * support for compressed input is GStreamer's, which a browser has no access to.
 */
@Injectable({ providedIn: 'root' })
export class RecordingTranscriber {
  /**
   * The words in a recording, as the note stores them. `parts` are the files the
   * recording is made of, in the order they were recorded; each is decoded by
   * itself and the audio is read as one. A part the platform finds undecodable
   * (`EncodingError`) is logged and left out, so it does not cost the note the
   * others. Throws where the recogniser refuses, a part fails to decode for any
   * other reason, or no part can be decoded, so the caller can leave the recording
   * to be tried again rather than marking a note read and losing a part.
   *
   * @param signal Stops the read: it rejects with an `AbortError` and releases the
   *   recogniser, whatever stage it has reached.
   */
  public async read(parts: Blob[], config: RecordingTranscriberConfig, signal?: AbortSignal): Promise<string> {
    const aborted = stoppedBy(signal);
    const samples = await Promise.race([this.#decode(parts), aborted]);
    if (!samples.length) {
      throw new Error('The recording holds no audio to read.');
    }

    const { SpeechConfig, ConversationTranscriber, AudioConfig, AudioInputStream, AudioStreamFormat, PhraseListGrammar } =
      await import('microsoft-cognitiveservices-speech-sdk');

    const speechConfig = SpeechConfig.fromSubscription(config.subscriptionKey, config.region);
    if (config.customEndpointId) {
      speechConfig.endpointId = config.customEndpointId;
    }
    if (config.language) {
      speechConfig.speechRecognitionLanguage = config.language;
    }

    const stream = AudioInputStream.createPushStream(
      AudioStreamFormat.getWaveFormatPCM(RECOGNISER_SAMPLE_RATE, 16, 1),
    );
    const transcriber = new ConversationTranscriber(speechConfig, AudioConfig.fromStreamInput(stream));
    if (config.phrases?.length) {
      try {
        PhraseListGrammar.fromRecognizer(transcriber).addPhrases(config.phrases);
      } catch (e) {
        console.warn('RecordingTranscriber: phrase list not applied', e);
      }
    }

    let segments: TranscriptSegment[] = [];
    const finished = new Promise<void>((resolve, reject) => {
      transcriber.transcribed = (_s, e) => {
        const text = e?.result?.text?.trim();
        if (text) {
          segments = appendSegment(segments, e?.result?.speakerId ?? null, text);
        }
      };
      // A push stream that has been closed ends the session once the recogniser
      // has read everything; a cancellation is the only other way out.
      transcriber.sessionStopped = () => resolve();
      transcriber.canceled = (_s, e) => {
        const detail = (e as { errorDetails?: string })?.errorDetails;
        // The SDK cancels with no error when the stream simply ran out, which is
        // the ordinary end of a recording rather than a failure.
        if (detail) {
          reject(new RecognitionCancelledError(detail));
        } else {
          resolve();
        }
      };
    });

    try {
      await Promise.race([
        new Promise<void>((resolve, reject) =>
          transcriber.startTranscribingAsync(() => resolve(), (e) => reject(new RecognitionCancelledError(String(e))))),
        aborted,
      ]);
      // Written whole before the stream is closed, so the recogniser sees the end
      // of the recording rather than waiting on a stream that never says it ended.
      stream.write(pcm16ToBytes(samples));
      stream.close();
      await Promise.race([finished, aborted]);
      await new Promise<void>((resolve) => transcriber.stopTranscribingAsync(() => resolve(), () => resolve()));
    } finally {
      try {
        transcriber.close();
      } catch {
        /* already disposed */
      }
    }

    return serializeTranscript(segments);
  }

  /**
   * Decodes each part to the one channel and rate the recogniser reads, joined in
   * order, leaving out a part that is undecodable. Throws any other failure, and
   * the last undecodable one where no part could be decoded.
   */
  async #decode(parts: Blob[]): Promise<Int16Array> {
    const decoder = new AudioContext();
    try {
      const decoded: Int16Array[] = [];
      let failure: unknown = null;
      for (const [index, part] of parts.entries()) {
        try {
          decoded.push(await this.#decodePart(decoder, part));
        } catch (e) {
          if (!isUndecodable(e)) {
            throw e;
          }
          failure = e;
          console.warn(`RecordingTranscriber: part ${index + 1} of ${parts.length} cannot be decoded and is left out`, e);
        }
      }
      if (!decoded.length && failure) {
        throw failure;
      }
      const joined = new Int16Array(decoded.reduce((total, samples) => total + samples.length, 0));
      let offset = 0;
      for (const samples of decoded) {
        joined.set(samples, offset);
        offset += samples.length;
      }
      return joined;
    } finally {
      await decoder.close().catch(() => undefined);
    }
  }

  /** Decodes one whole file, rendered at the recogniser's rate by the browser's own resampler. */
  async #decodePart(decoder: AudioContext, part: Blob): Promise<Int16Array> {
    const decoded = await decoder.decodeAudioData(await part.arrayBuffer());
    const target = new OfflineAudioContext(
      1,
      Math.max(1, Math.ceil(decoded.duration * RECOGNISER_SAMPLE_RATE)),
      RECOGNISER_SAMPLE_RATE,
    );
    const source = target.createBufferSource();
    source.buffer = decoded;
    source.connect(target.destination);
    source.start();
    const rendered = await target.startRendering();
    return toPcm16([rendered.getChannelData(0)]);
  }
}

/**
 * Rejects with an `AbortError` once `signal` stops the read, at once where it
 * already has, and never settles without one. The rejection is handled here, so
 * a read that ends before it is stopped leaves nothing unhandled.
 */
function stoppedBy(signal?: AbortSignal): Promise<never> {
  const stopped = new Promise<never>((_resolve, reject) => {
    if (signal?.aborted) {
      reject(readStopped());
    }
    signal?.addEventListener('abort', () => reject(readStopped()), { once: true });
  });
  stopped.catch(() => undefined);
  return stopped;
}

/**
 * Whether `error` is the platform finding a file undecodable: the Web Audio API's
 * `decodeAudioData` rejects with an `EncodingError` for data it cannot decode, and
 * with other errors for failures that may pass.
 */
function isUndecodable(error: unknown): boolean {
  return (error as { name?: unknown } | null)?.name === 'EncodingError';
}

/** The rejection of a read that was stopped. */
function readStopped(): DOMException {
  return new DOMException('The read was stopped.', 'AbortError');
}
